// Ink candidates for icons mounted on a studio header. #14181f rather than pure
// black so it sits softer on white without giving up contrast.
export const DARK_INK = '#14181f';
export const LIGHT_INK = '#ffffff';

export interface Rgb {
  r: number;
  g: number;
  b: number;
  a: number;
}

// Handles the two shapes we actually receive: `rgb()`/`rgba()` from
// getComputedStyle, and six-digit hex from our own ink constants.
export function parseColor(value: string): Rgb | null {
  const trimmed = (value || '').trim();

  const hex = /^#([0-9a-f]{6})$/i.exec(trimmed);
  if (hex) {
    const packed = parseInt(hex[1], 16);
    return {
      r: (packed >> 16) & 255,
      g: (packed >> 8) & 255,
      b: packed & 255,
      a: 1,
    };
  }

  const rgb = /^rgba?\(([^)]+)\)$/i.exec(trimmed);
  if (!rgb) return null;

  const parts = rgb[1]
    .split(/[,\s/]+/)
    .filter(Boolean)
    .map(Number);

  if (parts.length < 3 || parts.slice(0, 3).some((n) => !Number.isFinite(n))) {
    return null;
  }

  const alpha = parts.length > 3 && Number.isFinite(parts[3]) ? parts[3] : 1;
  return { r: parts[0], g: parts[1], b: parts[2], a: alpha };
}

function channelLuminance(channel: number): number {
  const scaled = channel / 255;
  return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
}

// WCAG 2.x relative luminance.
function relativeLuminance({ r, g, b }: Rgb): number {
  return (
    0.2126 * channelLuminance(r) +
    0.7152 * channelLuminance(g) +
    0.0722 * channelLuminance(b)
  );
}

function contrastRatio(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

const DARK_INK_LUMINANCE = relativeLuminance(parseColor(DARK_INK) as Rgb);
const LIGHT_INK_LUMINANCE = relativeLuminance(parseColor(LIGHT_INK) as Rgb);

// Returns whichever ink has the higher contrast ratio against `surface`.
// Comparing ratios rather than thresholding lightness keeps this correct by
// construction instead of correct by tuning. A surface we cannot read, or one
// that is fully transparent, is treated as white — the safe assumption for an
// unstyled page.
export function contrastInk(surface: string): string {
  const parsed = parseColor(surface);
  if (!parsed || parsed.a === 0) return DARK_INK;

  const surfaceLuminance = relativeLuminance(parsed);
  const onDark = contrastRatio(surfaceLuminance, DARK_INK_LUMINANCE);
  const onLight = contrastRatio(surfaceLuminance, LIGHT_INK_LUMINANCE);

  return onLight > onDark ? LIGHT_INK : DARK_INK;
}
